import { useInView } from "../hooks/useInView";
import { PhoneMockup } from "./PhoneMockup";

const steps = [
  {
    number: "01",
    title: "Learn",
    description:
      "Build real financial knowledge with bite-sized, structured lessons made for college students.",
    color: "text-brand-teal",
    bg: "bg-brand-teal",
    border: "border-brand-teal/30",
    screenshot: "/images/screenshots/learn.png",
  },
  {
    number: "02",
    title: "Practice",
    description:
      "Put your skills to the test in a 7-day life simulator where every decision has real consequences.",
    color: "text-brand-indigo",
    bg: "bg-brand-indigo",
    border: "border-brand-indigo/30",
    screenshot: "/images/screenshots/simulator.png",
  },
  {
    number: "03",
    title: "Plan",
    description:
      "Get a personalized action plan from your AI coach — tailored to your goals and situation.",
    color: "text-brand-purple",
    bg: "bg-brand-purple",
    border: "border-brand-purple/30",
    screenshot: "/images/screenshots/plan.png",
  },
  {
    number: "04",
    title: "Win",
    description:
      "Compete with peers in weekly challenges and earn real scholarship opportunities.",
    color: "text-brand-gold",
    bg: "bg-brand-gold",
    border: "border-brand-gold/30",
    screenshot: "/images/screenshots/leaderboard.png",
  },
];

export function HowItWorks() {
  const [ref, inView] = useInView();

  return (
    <section id="how-it-works" className="relative py-24 bg-bg-deep overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-brand-teal font-semibold tracking-wide uppercase text-sm mb-3">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Four steps to{" "}
            <span className="text-brand-teal">financial confidence</span>
          </h2>
          <p className="mt-4 text-text-secondary text-lg max-w-2xl mx-auto">
            A simple path from learning to earning — all inside one app.
          </p>
        </div>

        {/* Desktop: horizontal step flow */}
        <div
          ref={ref}
          className="hidden lg:grid grid-cols-4 gap-6 items-start"
        >
          {steps.map((step, i) => (
            <div
              key={step.title}
              className={`relative flex flex-col items-center text-center ${
                inView
                  ? `animate-fade-up animate-delay-${(i + 1) * 100}`
                  : "start-hidden"
              }`}
            >
              {/* Connector arrow (between steps) */}
              {i < steps.length - 1 && (
                <div className="absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] flex items-center z-0">
                  <div className="flex-1 h-px bg-gradient-to-r from-border-primary to-border-primary/40" />
                  <svg
                    className="w-3 h-3 text-border-primary -ml-px flex-shrink-0"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path d="M2 1l8 5-8 5V1z" />
                  </svg>
                </div>
              )}

              {/* Step number badge */}
              <div
                className={`relative z-10 w-16 h-16 rounded-2xl ${step.bg}/15 border ${step.border} flex items-center justify-center mb-5`}
              >
                <span className={`text-xl font-bold ${step.color}`}>
                  {step.number}
                </span>
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold mb-2 ${step.color}`}>
                {step.title}
              </h3>

              {/* Description — fixed height so phones align */}
              <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-[220px] min-h-[6rem]">
                {step.description}
              </p>

              {/* Phone mockup */}
              <div className="transition-transform duration-300 hover:scale-105">
                <PhoneMockup src={step.screenshot} alt={step.title} />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: vertical step flow */}
        <div className="lg:hidden flex flex-col gap-12">
          {steps.map((step, i) => (
            <div key={step.title} className="flex flex-col items-center text-center">
              {/* Connector line (above, except first) */}
              {i > 0 && (
                <div className="w-px h-8 bg-gradient-to-b from-border-primary/40 to-border-primary mb-4" />
              )}

              {/* Step number badge */}
              <div
                className={`w-14 h-14 rounded-2xl ${step.bg}/15 border ${step.border} flex items-center justify-center mb-4`}
              >
                <span className={`text-lg font-bold ${step.color}`}>
                  {step.number}
                </span>
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold mb-2 ${step.color}`}>
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-text-secondary text-sm leading-relaxed mb-5 max-w-xs">
                {step.description}
              </p>

              {/* Phone mockup */}
              <PhoneMockup src={step.screenshot} alt={step.title} width={220} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
